import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { cargosApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton'; 
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Briefcase,
} from 'lucide-react';
import { toast } from 'sonner';

export const Cargos = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cargos, setCargos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, cargo: null });

  useEffect(() => {
    fetchCargos();
  }, [search]);

  const fetchCargos = async () => {
    try {
      const response = await cargosApi.list({
        igreja_id: user?.igreja_id,
        search: search || undefined,
      });
      setCargos(response.data || []);
    } catch (error) {
      console.error('Error fetching cargos:', error);
      toast.error('Erro ao carregar cargos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await cargosApi.delete(deleteDialog.cargo.id);
      toast.success('Cargo excluído com sucesso');
      setDeleteDialog({ open: false, cargo: null });
      fetchCargos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir cargo');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="cargos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Cargos</h1>
          <p className="page-subtitle">Gerencie os cargos da sua igreja</p>
        </div>
        <Button
          onClick={() => navigate('/cargos/novo')}
          className="btn-primary"
          data-testid="add-cargo-btn"
        >
          <Plus className="mr-2 h-4 w-4" />
          Novo Cargo
        </Button>
      </div>

      {/* Search */}
      <Card className="stat-card">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Buscar por nome..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 input-field"
              data-testid="search-cargos"
            />
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="stat-card">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-sky-50">
              <Briefcase className="w-5 h-5 text-sky-500" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total de Cargos</p>
              <p className="text-2xl font-bold text-slate-900">{cargos.length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="stat-card overflow-hidden" data-testid="cargos-table">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : cargos.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className="mx-auto h-12 w-12 text-slate-300" />
              <h3 className="mt-4 text-lg font-medium text-slate-900">Nenhum cargo encontrado</h3>
              <p className="mt-2 text-slate-500">Comece adicionando um novo cargo à sua igreja.</p>
              <Button
                onClick={() => navigate('/cargos/novo')}
                className="mt-4 btn-primary"
              >
                <Plus className="mr-2 h-4 w-4" />
                Adicionar Cargo
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Nome</TableHead>
                    <TableHead className="hidden md:table-cell">Descrição</TableHead>
                    <TableHead className="hidden lg:table-cell">Ativo</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cargos.map((cargo) => (
                    <TableRow key={cargo.id} className="hover:bg-slate-50" data-testid={`cargo-row-${cargo.id}`}>
                      <TableCell className="font-medium text-slate-900">{cargo.nome}</TableCell>
                      <TableCell className="hidden md:table-cell text-slate-600">
                        {cargo.descricao || '-'}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {cargo.ativo !== false ? (
                          <Badge variant="secondary" className="bg-green-50 text-green-700">
                            Ativo
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-red-50 text-red-700">
                            Inativo
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`cargo-actions-${cargo.id}`}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/cargos/${cargo.id}/editar`)}>
                              <Edit className="mr-2 h-4 w-4" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => setDeleteDialog({ open: true, cargo })}
                              className="text-red-600"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, cargo: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Cargo</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o cargo "{deleteDialog.cargo?.nome}"?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Cargos;
